'use client';
// Aperçu PDF officiel d'un document maîtrisé (D1).
//
// Ce que l'on montre n'est pas le contenu brut : c'est le document tel qu'il
// sortira sur papier, cartouche compris — code, version, statut, date de
// vigueur, signataires, mention de copie non maîtrisée, pagination. Le
// cartouche est fabriqué par le serveur à partir des métadonnées de la
// plateforme : il n'existe pas de second cartouche saisi à la main qui pourrait
// diverger.
//
// L'aperçu est ABSENT tant que le document est en rédaction : un brouillon
// d'allure officielle est exactement ce qui ne doit pas circuler.
import React, { useEffect, useRef, useState } from 'react';
import { useGed } from '@/lib/store';
import { apercuTelQuel, etatApercu, peut } from '@/lib/meta';
import { versionApplicable, versionCandidate, revisionEnCours } from '@/lib/version-en-cours';
import { I, EmptyState } from '@/components/ui';
import type { GedDocument } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
/**
 * `version` : le numéro de version à montrer. Par défaut la version APPLICABLE
 * dès qu'il en existe une (RQ, 06/10/2026 : « au premier abord je voudrais voir
 * la version en cours ») — la fiche passe le numéro du candidat quand on a
 * explicitement demandé à voir le travail en chantier.
 */
export function ApercuTab({ doc, version }: { doc: GedDocument; version?: string }) {
  const { session, settings, showToast } = useGed();
  const [url, setUrl] = useState('');
  const [erreur, setErreur] = useState('');
  const [charge, setCharge] = useState(false);
  const [dl, setDl] = useState(false);
  // Révoquer l'URL d'objet précédente : sans ça, chaque changement de document
  // laisse un PDF entier en mémoire jusqu'au rechargement de la page.
  const urlRef = useRef('');

  // « Tel quel » = externe (DE) ou PDF déposé : pas de cartouche. Un document rédigé
  // ici puis déposé en Word reçoit cartouche et filigrane comme les autres.
  const estExterne = apercuTelQuel(doc);
  /* QUELLE VERSION CET APERÇU MONTRE-T-IL ? Pendant une révision le document a
     deux versions vivantes, et la réponse change tout : ce qui s'applique se
     lit et s'imprime, le chantier non. MÊME calcul que le serveur. */
  const appl = versionApplicable(doc.versions);
  const cand = versionCandidate(doc.versions);
  const revision = revisionEnCours(doc);
  const num = version || String(appl?.num || cand?.num || doc.versionCourante || '');
  const estCandidate = !!cand && String(cand.num) === num;
  const estApplicable = !!appl && String(appl.num) === num;
  /* Trois genres, trois régimes — MÊME résolution que le serveur : ce qui
     s'applique (lisible, imprimable), le chantier (grisé, filigrané), le passé
     (lisible, jamais imprimable). */
  const genre = estCandidate ? 'candidate' as const : estApplicable ? 'applicable' as const : 'obsolete' as const;
  const etat = etatApercu(doc.statut, estExterne ? 'importe' : '', {
    revisionEnCours: revision,
    versionDemandee: genre,
  });
  const estRQ = session.roles.includes('rq');
  /* EN CIRCUIT DE VALIDATION (RQ, 04/10/2026) : on voit le document, GRISÉ —
     pour qu'on comprenne d'un regard qu'il n'est pas encore applicable. Le PDF
     porte lui-même le filigrane « EN COURS DE VALIDATION — NE PAS APPLIQUER » et
     ne se télécharge pas (le serveur refuse). */
  // Un document en circuit est RÉDIGÉ ici, quel que soit le format déposé (`classe`).
  /* ⚠️ GRISER LA BONNE CHOSE (06/10/2026) : la condition ne regardait que le
     statut, si bien que pendant la revue d'une RÉVISION on grisait la version
     APPLICABLE — celle qui s'applique — comme si elle était en validation.
     C'est l'exemplaire SERVI qui décide : grisé seulement s'il est le candidat. */
  const enCircuit = estCandidate && ['averifier', 'aapprouver'].includes(doc.statut);
  /* Un exemplaire sous licence se CONSULTE et ne se récupère pas : le bouton
     disparaît pour tout le monde sauf le RQ, qui détient la licence et doit
     pouvoir le produire en audit. Le serveur refuse de toute façon — l'écran
     ne fait que ne pas proposer un geste voué au refus. */
  const sousLicence = estExterne && doc.telechargementRestreint === true && !estRQ;
  const peutImprimer = etat.imprimable && !sousLicence
    && (estExterne ? estRQ || !doc.telechargementRestreint : peut(settings?.perms, session.profile, 'imprimer'));

  useEffect(() => {
    if (!etat.visible) return;
    let vivant = true;
    void (async () => {
      setCharge(true); setErreur('');
      try {
        const r = await fetch(`/api/ged/ged/docs/${doc.documentId}/apercu.pdf?version=${encodeURIComponent(num)}`, { credentials: 'include' });
        if (!r.ok) {
          const d = await r.json().catch(() => null);
          throw new Error(d?.error?.message || d?.error || 'L’aperçu n’a pas pu être produit.');
        }
        const blob = await r.blob();
        if (!vivant) return;
        const u = URL.createObjectURL(blob);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = u;
        setUrl(u);
      } catch (e) {
        if (vivant) setErreur((e as Error).message);
      } finally {
        if (vivant) setCharge(false);
      }
    })();
    return () => { vivant = false; };
    // `fichier.updatedAt` change à chaque enregistrement du fichier : l'aperçu
    // se régénère donc dès que le contenu réel bouge, sans action de
    // l'utilisateur. PAS `fichier.key` : elle ne change plus qu'à la fermeture
    // de la session OnlyOffice, un Ctrl+S ne la touche pas (voir office.ts).
  }, [etat.visible, doc.documentId, doc.statut, num, doc.fichier?.key, doc.fichier?.updatedAt]);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  // Le téléchargement passe par une seconde requête `?telecharger=1` : c'est
  // elle que le serveur contrôle et journalise. Réutiliser le blob déjà affiché
  // produirait une copie non tracée.
  const telecharger = async () => {
    setDl(true);
    try {
      const r = await fetch(`/api/ged/ged/docs/${doc.documentId}/apercu.pdf?telecharger=1&version=${encodeURIComponent(num)}`, { credentials: 'include' });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        showToast(d?.error?.message || d?.error || 'Téléchargement refusé.', 'erreur');
        return;
      }
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${doc.code}-v${num}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      showToast('Copie éditée et tracée au journal — elle porte la mention « copie non maîtrisée ».');
    } finally {
      setDl(false);
    }
  };

  if (!etat.visible) {
    return (
      <div style={{ padding: 22 }}>
        <EmptyState
          titre={['averifier', 'aapprouver'].includes(doc.statut)
            ? 'Pas d’aperçu tant que le document est en circuit de validation.'
            : 'Pas d’aperçu tant que le document est en rédaction.'}
          indice={etat.raison}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 18, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {/* La légende dit ce que l'écran montre RÉELLEMENT, et les deux cas ne
            sont pas le même document. Sur un externe, annoncer « cartouche,
            signataires et pagination produits par la plateforme » serait faux :
            on n'a rien produit du tout — on affiche l'exemplaire tel qu'il a été
            déposé. Une légende qui décrit autre chose que ce qu'on voit est
            exactement ce qui fait douter de tout le reste de l'écran. */}
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 1, minWidth: 220 }}>
          {enCircuit ? (
            <>{etat.raison} Ni téléchargeable ni imprimable : il est affiché grisé tant qu’il n’est pas approuvé.</>
          ) : genre === 'obsolete' ? (
            <>
              Version <strong>obsolète</strong> — exemplaire archivé, conservé tel qu’il faisait foi.
              Ni téléchargeable ni imprimable : chaque page porte le filigrane « ne pas appliquer ».
              Le cartouche nomme les signataires <strong>de cette version</strong> et la période où elle s’appliquait.
            </>
          ) : estExterne ? (
            <>
              Exemplaire détenu par le laboratoire, affiché <strong>tel qu’il a été déposé</strong> —
              sans cartouche ni pagination ajoutés : ce document n’est pas rédigé ici, et la plateforme
              n’y touche pas.
              {sousLicence
                ? ' Il est sous licence : il se consulte à l’écran et ne se télécharge pas.'
                : ''}
            </>
          ) : (
            <>
              Document tel qu’il s’imprime — cartouche, signataires et pagination sont produits par la
              plateforme. {etat.imprimable
                ? 'Toute copie éditée porte la mention « copie non maîtrisée » et est tracée au journal.'
                : enCircuit
                  ? `${etat.raison} Ni téléchargeable ni imprimable : chaque page porte le filigrane « en cours de validation — ne pas appliquer ».`
                  : `Non imprimable : ${etat.raison.toLowerCase()} Chaque page porte le filigrane « ne pas appliquer ».`}
            </>
          )}
        </span>
        {peutImprimer && (
          <Bouton variante="primaire" taille="sm" onClick={() => void telecharger()} disabled={dl || !url}>
            <I n="print" s={14} />{dl ? 'Édition…' : 'Télécharger / imprimer'}
          </Bouton>
        )}
      </div>

      {charge && (
        <div style={{ padding: '40px 18px', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
          Génération de l’aperçu officiel…
        </div>
      )}

      {erreur && !charge && (
        <div role="alert" style={{ background: 'var(--red-50)', color: 'var(--red-600)', borderRadius: 'var(--radius-md)', padding: '12px 14px', fontSize: 13, lineHeight: 1.55 }}>
          {erreur}
        </div>
      )}

      {url && !charge && enCircuit && (
        <div role="note" style={{ background: 'var(--gray-100)', color: 'var(--text-strong)', border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: 12.5, fontWeight: 600 }}>
          Document en cours de validation — consultation seulement. Il n’est pas applicable tant qu’il n’est pas approuvé et diffusé.
        </div>
      )}

      {url && !charge && (
        <div style={{ position: 'relative' }}>
        {/* `object` plutôt qu'`iframe` : le contenu de repli s'affiche quand le
            navigateur n'a pas de visualiseur PDF intégré, au lieu d'un cadre noir
            sans explication. */}
        <object
          /* `#toolbar=0` : en circuit, la barre du lecteur PDF du navigateur (avec SES
             boutons télécharger / imprimer) est masquée — sinon le refus du serveur
             se contournait par le lecteur lui-même. */
          data={enCircuit ? `${url}#toolbar=0` : url} type="application/pdf" aria-label={`Aperçu ${doc.code}`}
          style={{ width: '100%', height: 'min(78vh, 900px)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', background: 'var(--gray-25)', display: 'block',
            ...(enCircuit ? { filter: 'grayscale(1)', opacity: 0.6 } : {}) }}>
          <div style={{ padding: '30px 18px', textAlign: 'center', fontSize: 13, color: 'var(--text-body)' }}>
            Votre navigateur n’affiche pas les PDF dans la page.
            <div style={{ marginTop: 10 }}>
              <a href={url} target="_blank" rel="noreferrer" className="ged-btn" style={{ ...styleBouton('contour', 'sm'), textDecoration: 'none' }}>
                Ouvrir l’aperçu dans un onglet
              </a>
            </div>
          </div>
        </object>
        {/* Voile gris PAR-DESSUS : le filtre CSS ne s'applique pas à tous les
            visualiseurs PDF intégrés ; le voile, lui, se voit partout. Il laisse
            passer la souris — on fait défiler et on zoome comme d'habitude. */}
        {enCircuit && (
          <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'rgba(120, 124, 132, 0.28)', borderRadius: 'var(--radius-md)', pointerEvents: 'none' }} />
        )}
        </div>
      )}
    </div>
  );
}
